"use client";
import type React from "react";
import { useState, useRef, useEffect } from "react";
import { Flex, Heading, Input, IconButton, useToast } from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";

import { phoneNumberService } from "../../voice-services/phoneNumberService";
import {
  formatPhonePretty,
  isLikelyUsPhoneLabel,
} from "../../voice-services/voicePhoneDisplay";

interface EditablePhoneNumberNameProps {
  phoneNumber: string;
  nickname: string | null;
  prettyNumber: string;
  onUpdate: () => void;
}

export const EditablePhoneNumberName: React.FC<
  EditablePhoneNumberNameProps
> = ({ phoneNumber, nickname, prettyNumber, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentNickname, setCurrentNickname] = useState(
    nickname || prettyNumber || phoneNumber
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  useEffect(() => {
    setCurrentNickname(nickname || prettyNumber || phoneNumber);
  }, [nickname, prettyNumber, phoneNumber]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const nick = nickname?.trim() ?? "";
  const headingDisplay =
    !nick
      ? (prettyNumber || phoneNumber).trim()
      : isLikelyUsPhoneLabel(nick)
        ? formatPhonePretty(nick)
        : nick;

  const handleEdit = () => {
    setCurrentNickname(nickname || prettyNumber || phoneNumber);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const trimmedNickname = currentNickname.trim();

    if (!trimmedNickname) {
      setCurrentNickname(prettyNumber || phoneNumber);
      setIsEditing(false);
      return;
    }

    try {
      await phoneNumberService.update(phoneNumber, {
        nickname: trimmedNickname,
      });

      toast({
        title: "Nickname updated",
        status: "success",
        duration: 3000,
      });
      onUpdate();

      setIsEditing(false);
    } catch (error: any) {
      console.error("Error updating nickname:", error);
      toast({
        title: "Error updating nickname",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 3000,
      });

      setCurrentNickname(nickname || prettyNumber || phoneNumber);
      setIsEditing(false);
    }
  };

  return (
    <Flex align="left">
      {isEditing ? (
        <Input
          ref={inputRef}
          value={currentNickname}
          onChange={(e) => setCurrentNickname(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSave();
            } else if (e.key === "Escape") {
              setCurrentNickname(nickname || prettyNumber || phoneNumber);
              setIsEditing(false);
            }
          }}
          size="lg"
          width="auto"
          height="auto"
          fontWeight="medium"
          variant="filled"
          borderColor="transparent"
          _focus={{ borderColor: "transparent" }}
          outline="none"
        />
      ) : (
        <>
          <Heading size="md" fontWeight="medium">
            {headingDisplay}
          </Heading>
          <IconButton
            aria-label="Edit nickname"
            icon={<EditIcon />}
            size="xs"
            variant="ghost"
            ml={1}
            onClick={handleEdit}
            color={"gray.500"}
            _hover={{
              color: "gray.800",
              bg: "gray.200",
            }}
          />
        </>
      )}
    </Flex>
  );
};
